import { QRCodeSVG } from 'qrcode.react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'
import { OtpDigitInput } from '../atoms/OtpDigitInput'

/**
 * TotpEnrollStep — the screen the user sees when the pipeline runtime
 * pauses at `credential_set` with `factor: "totp"` during enrollment.
 *
 * Composition: QR code (qrcode.react SVG) + "Can't scan?" disclosure
 * that reveals the Base32 secret as a fallback for manual entry +
 * 6-cell OtpDigitInput for the confirm code + Verify / Cancel buttons.
 *
 * No network calls — `onSubmit` and `onCancel` come from the parent so
 * the same molecule works for any registration / step-up flow that asks
 * the user to register an authenticator app.
 *
 * A11y:
 *   - QR image has explicit `aria-label` and a sibling text fallback
 *     announcing the alternative manual-entry secret path.
 *   - Secret reveal is a real toggle button (`aria-expanded`) and the
 *     revealed secret is a `<code>` element so AT users can copy it.
 *   - Inline error is `role="alert"` + `aria-live="polite"`.
 *   - When `error` is non-empty every code cell is `aria-invalid="true"`.
 */

export interface TotpEnrollStepProps {
  /**
   * The otpauth URI returned by the backend's TOTP-enroll begin step
   * (e.g. `otpauth://totp/Cymmetri:alice@example.com?secret=...&issuer=Cymmetri`).
   * Encoded into the QR for scanning; the secret is also extracted from
   * the URI so the manual-entry fallback works without an extra prop.
   */
  otpauthUri: string
  /**
   * Base32-encoded secret. Displayed under the "Can't scan?" disclosure
   * for manual entry. Treat this string as sensitive — do not log it,
   * do not persist it in localStorage. The parent should clear it from
   * state once the enrollment is confirmed.
   */
  secretB32: string
  /** Number of cells in the confirm-code input. Default 6 (TOTP RFC 6238 default). */
  codeLength?: number
  /** Called when the user submits the confirm code (Verify button or Enter). */
  onSubmit: (code: string) => void | Promise<void>
  /**
   * Called when the user clicks the "Cancel enrollment" button. Optional —
   * when absent, no cancel button is rendered (some flows have no escape
   * hatch mid-pipeline; the parent decides).
   */
  onCancel?: () => void
  /**
   * Called when the user clicks the "Skip for now" tertiary button. Renders
   * only when the parent passes onSkip (driven by the pause envelope's
   * can_skip field — the BE evaluator sets it when the credential_set block
   * was configured with `optional: true`). The parent issues the resume
   * with `{ action: "skip" }`; the BE advances without enrolling.
   */
  onSkip?: () => void | Promise<void>
  /** Override the skip button label. Default: "Skip for now". */
  skipLabel?: string
  /** Inline error surfaced by the parent (e.g. "code didn't match"). */
  error?: string
  /** Set true while submit is in flight. Disables inputs + shows "Verifying…". */
  busy?: boolean
  /**
   * Override the visible heading. Default: "Set up your authenticator app".
   * Use for step-up / re-enrollment flows where wording should differ.
   */
  heading?: string
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /** Extra classes for the wrapper. */
  className?: string
}

export const TotpEnrollStep = React.forwardRef<HTMLDivElement, TotpEnrollStepProps>(
  (
    {
      otpauthUri,
      secretB32,
      codeLength = 6,
      onSubmit,
      onCancel,
      onSkip,
      skipLabel = 'Skip for now',
      error,
      busy = false,
      heading = 'Set up your authenticator app',
      helperText = 'Scan this QR code with Google Authenticator, 1Password, or any other TOTP app. Then enter the 6-digit code below.',
      className,
    },
    ref,
  ) => {
    const [code, setCode] = React.useState('')
    const [secretRevealed, setSecretRevealed] = React.useState(false)

    // Reset code when the pause identity changes (different otpauthUri =
    // different enrollment session; old digits become invalid). Without
    // this, a stale code from a prior attempt could auto-paste forward
    // and immediately fail the verify (same single-strike pattern that
    // the VerificationOtpStep digit-reset closes).
    // biome-ignore lint/correctness/useExhaustiveDependencies: setCode is a stable React state setter; re-fire only on enrollment-session identity change
    React.useEffect(() => {
      setCode('')
      setSecretRevealed(false)
    }, [otpauthUri])

    // Also clear the code when a fresh error arrives. The BE soft-deny
    // path re-emits the same otpauth_uri (same enrollment session) but
    // adds challenge.error -- so the above otpauthUri-keyed reset does
    // NOT fire, and the user is left staring at the digits that just
    // failed. Reset on every error transition so the next attempt is
    // unambiguous.
    // biome-ignore lint/correctness/useExhaustiveDependencies: setCode is a stable React state setter; intentional re-fire on each new error message
    React.useEffect(() => {
      if (error) setCode('')
    }, [error])

    const handleSubmit = React.useCallback(async () => {
      if (busy) return
      if (code.length !== codeLength) return
      await onSubmit(code)
    }, [busy, code, codeLength, onSubmit])

    const submitDisabled = busy || code.length !== codeLength

    return (
      <div ref={ref} className={cn('flex flex-col gap-5', className)}>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="text-heading-sm font-semibold text-ia-text">{heading}</h2>
          {helperText ? <p className="text-body-sm text-ia-text-secondary">{helperText}</p> : null}
        </div>

        {/* QR + manual fallback live in a centered column so a small
            viewport (mobile portrait, narrow modal) keeps both visible
            without horizontal scroll. */}
        <div className="flex flex-col items-center gap-3">
          <div
            role="img"
            aria-label="QR code containing your authenticator setup link"
            className="rounded-md border border-ia-border bg-white p-3"
          >
            <QRCodeSVG value={otpauthUri} size={176} level="M" includeMargin={false} />
          </div>

          <button
            type="button"
            onClick={() => setSecretRevealed((v) => !v)}
            aria-expanded={secretRevealed}
            aria-controls="totp-enroll-secret"
            className="text-body-sm text-primary hover:underline focus-visible:underline"
          >
            {secretRevealed ? 'Hide secret' : "Can't scan? Show secret"}
          </button>

          {secretRevealed ? (
            <div id="totp-enroll-secret" className="flex flex-col items-center gap-1 text-center">
              <p className="text-body-sm text-ia-text-secondary">
                Enter this code in your authenticator app:
              </p>
              <code className="select-all rounded-sm bg-surface px-2 py-1 font-mono text-body-sm text-ia-text">
                {secretB32}
              </code>
            </div>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-center text-body-sm text-ia-text-secondary">
            Enter the {codeLength}-digit code your app shows.
          </p>
          <OtpDigitInput
            value={code}
            onChange={setCode}
            onEnter={handleSubmit}
            length={codeLength}
            autoFocus
            disabled={busy}
            invalid={!!error}
            aria-label="authenticator confirm code"
          />
        </div>

        {/* Reserve a row for the error so layout doesn't jump. */}
        <div
          role="alert"
          aria-live="polite"
          className={cn(
            'min-h-5 text-center text-body-sm',
            error ? 'text-ia-error' : 'text-transparent',
          )}
        >
          {error || 'placeholder'}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            disabled={submitDisabled}
            loading={busy}
            className="w-full"
          >
            {busy ? 'Verifying…' : 'Verify and enable'}
          </Button>
          {onCancel ? (
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={onCancel}
              disabled={busy}
              className="w-full"
            >
              Cancel enrollment
            </Button>
          ) : null}
          {onSkip ? (
            // Tertiary "Skip for now" — only rendered when parent passes
            // onSkip (driven by env.can_skip from the optional config knob
            // on the BE credential_set block). Plain text styling keeps the
            // primary Verify path visually dominant.
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                void onSkip()
              }}
              className={cn(
                'mx-auto rounded-md px-3 py-1.5 text-body-sm font-medium text-ia-text-secondary',
                'transition-colors duration-150 hover:text-ia-text',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {skipLabel}
            </button>
          ) : null}
        </div>
      </div>
    )
  },
)
TotpEnrollStep.displayName = 'TotpEnrollStep'
